/**
 * Our own products — the list behind /products, the nav, the contact form and llms.txt.
 *
 * Plain ESM with no aliases, so the Node build scripts can import it too.
 */
import { EDUANANT } from "./eduanant.js";

/**
 * SNAPXOps facts, as stated on this site. The authority is the SNAPXOps repository:
 * `README.md`, `docs/SNAPXOps_Product_Architecture_Plan.md` (§2 vision, §4.1 releases)
 * and the sign-in page copy in `apps/web/src/i18n/en.json`.
 *
 * Rules, because this product is not live yet:
 *   • Describe only what Release 1 contains as built (plan §4.1, "R1"). Later releases
 *     go under `comingNext`, without dates — the plan's dates are targets, not promises.
 *   • No prices. The commercial pack is drafted but awaits management sign-off.
 *   • The first customer, ABS Group, is named with the product owner's confirmation
 *     (20 Oct 2026). Their details are as published on absgroupindia.com.
 * Update `status` and `statusLine` when the first installation goes live.
 */
export const SNAPXOPS = {
  slug: "snapxops",
  name: "SNAPXOps",
  status: "Early access",
  statusLine: "Our first installation, at ABS Group in Noida, goes live in October 2026.",
  firstCustomer: {
    name: "ABS Group",
    legalName: "ABS Tool and Die Private Limited",
    place: "Noida, Uttar Pradesh",
    since: "2013",
    url: "https://absgroupindia.com",
    logo: "/assets/clients/abs.webp",
    detail:
      "An ISO 9001:2015 certified maker of precision components, tooling, jigs and fixtures and automated production systems for the defence, aerospace and commercial sectors.",
  },
  segment: "tool rooms and precision job shops",
  makes: "gauges, fixtures, press tools, dies, moulds and special-purpose machines",

  /** The problem, in the words a PPC coordinator would use. */
  problems: [
    { title: "History is overwritten", body: "The status cell changes on every move, so how long a job sat anywhere is lost — and with it cycle time, ageing and bottlenecks." },
    { title: "Status travels by phone", body: "The PPC workbook is only as current as the last person who edited it. Everyone else calls to ask." },
    { title: "Jobs are cut and pasted", body: "A job moves between sheets as it progresses, and the department column is typed by hand." },
    { title: "Customers are a name in a cell", body: "No contacts, no order grouping, and no quick answer to “what are we waiting on from this customer?”" },
  ],

  /** Release 1, as built. */
  features: [
    { key: "route", title: "A route card for every job", body: "Each job gets its own ordered list of operations from a template, edited for that job — and a print-ready route card with your name and logo that travels with the part." },
    { key: "board", title: "A live board and queue", body: "Every job in every department, updating as people work. Departments move their own jobs on from a phone or a shared PC, in a few taps." },
    { key: "dashboard", title: "Overdue, counted for you", body: "Live jobs, overdue jobs, jobs waiting on the customer, work in progress by department, ageing, and the department where work is piling up." },
    { key: "history", title: "Nothing is ever lost", body: "Every move is kept with who made it and when, so time spent in each status is measured, not guessed. An audit log records every change." },
    { key: "customers", title: "Customers, ready for billing", body: "Company details, GSTIN with validation, vendor codes, contacts and addresses, and every job for each customer." },
    { key: "config", title: "Your process, not ours", body: "Statuses, departments, transitions and route templates are settings your admin edits. Your existing Excel workbook is imported, with a report on every row." },
  ],

  /** Commitments that hold from day one. */
  assurances: [
    { title: "Your own installation", body: "Each customer gets its own server and its own database. Nothing is shared with any other company." },
    { title: "Backed up, offsite", body: "Automatic backups every few hours, checked as they are made, with encrypted copies in your own Google Drive." },
    { title: "Secure sign-in", body: "Two-factor sign-in for admins and owners, roles for every kind of user, and personal details stored encrypted." },
    { title: "Your data, whenever you want it", body: "Export any view to Excel, or everything in one file. Our support signs in only while your admin lets us in." },
  ],

  /** Planned after Release 1. No dates on purpose. */
  comingNext: ["Hindi throughout", "Supply-order screens", "Custom fields", "Scheduled PDF and Excel reports by email", "Undo for moves"],

  techStack: ["React", "TypeScript", "NestJS", "PostgreSQL", "Docker"],
};

/** Every product, in display order. `path` is the page on this site. */
export const PRODUCTS = [
  {
    slug: "eduanant",
    name: "EduAnant",
    path: "/products/eduanant",
    externalUrl: EDUANANT.url,
    status: `Live since ${EDUANANT.liveSchool.since}`,
    live: true,
    accent: "#38BDF8",
    audience: "Schools",
    tagline: "Run your entire school from one place.",
    summary: `School management software for Indian schools — ${EDUANANT.modules} modules from admissions and fees to HR and library, with teacher and parent portals and an Android app rated ${EDUANANT.playStore.rating}★ on Google Play.`,
    contactLabel: "EduAnant (school software)",
  },
  {
    slug: SNAPXOPS.slug,
    name: SNAPXOPS.name,
    path: "/products/snapxops",
    externalUrl: null,
    status: SNAPXOPS.status,
    live: false,
    accent: "#F59E0B",
    audience: "Tool rooms & job shops",
    tagline: "Every job, every route card, live.",
    summary: `Job and route-card tracking for ${SNAPXOPS.segment} — a live board for every department, overdue work counted for you, and a history that is never overwritten. First customer: ${SNAPXOPS.firstCustomer.name}, ${SNAPXOPS.firstCustomer.place.split(",")[0]}.`,
    contactLabel: "SNAPXOps (tool room tracking)",
  },
];
